/**
 * ESocialEventFormModal Component
 * Modal for creating and editing eSocial events
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  FormHelperText,
  Divider,
  Typography
} from '@mui/material';
import {
  ESocialEvent,
  ESocialEventType,
  ESocialEventFormData,
  ESOCIAL_EVENT_TYPE_LABELS,
  eventTypeRequiresEmployee
} from '../../../../../types/esocial';
import { S2210Metadata, S2220Metadata, S2240Metadata, getDefaultMetadata } from '../../../../../types/esocial-forms';
import S2210Form from '../forms/S2210Form';
import S2220Form from '../forms/S2220Form';
import S2240Form from '../forms/S2240Form';

interface ESocialEventFormModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: ESocialEventFormData, metadata?: Record<string, unknown> | null) => Promise<void>;
  event?: ESocialEvent;
  employees?: Array<{ id: number; fullName: string }>;
  error?: string | null;
}

const getDefaultFormData = (): ESocialEventFormData => ({
  eventType: '',
  eventDate: new Date().toISOString().split('T')[0],
  employeeId: '',
  dueDate: '',
  notes: ''
});

const ESocialEventFormModal: React.FC<ESocialEventFormModalProps> = ({
  open,
  onClose,
  onSubmit,
  event,
  employees = [],
  error
}) => {
  const [formData, setFormData] = useState<ESocialEventFormData>(getDefaultFormData());
  const [metadata, setMetadata] = useState<Record<string, unknown> | null>(null);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const isEditing = !!event;

  useEffect(() => {
    if (open) {
      if (event) {
        setFormData({
          eventType: event.eventType,
          eventDate: event.eventDate.split('T')[0],
          employeeId: event.employeeId?.toString() || '',
          dueDate: event.dueDate?.split('T')[0] || '',
          notes: event.notes ?? ''
        });
        setMetadata(event.metadata ?? null);
      } else {
        setFormData(getDefaultFormData());
        setMetadata(null);
      }
      setValidationErrors({});
    }
  }, [open, event]);

  const validate = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.eventType) {
      errors.eventType = 'Tipo de evento é obrigatório';
    }

    if (!formData.eventDate) {
      errors.eventDate = 'Data do evento é obrigatória';
    }

    if (formData.eventType && eventTypeRequiresEmployee(formData.eventType as ESocialEventType) && !formData.employeeId) {
      errors.employeeId = 'Funcionário é obrigatório para este tipo de evento';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setLoading(true);
    try {
      await onSubmit(formData, metadata);
      onClose();
    } catch (_err) {
      // Error is handled by parent
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: keyof ESocialEventFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear validation error when field is changed
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
    // Init default metadata when event type changes
    if (field === 'eventType' && !metadata) {
      const defaults = getDefaultMetadata(value);
      if (defaults) setMetadata(defaults as Record<string, unknown>);
    }
  };

  const requiresEmployee = formData.eventType
    ? eventTypeRequiresEmployee(formData.eventType as ESocialEventType)
    : false;

  // Garante que o funcionário já vinculado apareça ao editar, mesmo que não
  // esteja na lista carregada da empresa (ex.: inativo/filtrado).
  const employeeOptions = useMemo<Array<{ id: number; fullName: string }>>(() => {
    const selected = event?.employee;
    if (selected && !employees.some((emp) => emp.id === selected.id)) {
      return [{ id: selected.id, fullName: selected.name }, ...employees];
    }
    return employees;
  }, [employees, event]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Evento eSocial' : 'Novo Evento eSocial'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Grid container spacing={2}>
            {/* Event Type */}
            <Grid size={12}>
              <FormControl fullWidth error={!!validationErrors.eventType}>
                <InputLabel>Tipo de Evento *</InputLabel>
                <Select
                  value={formData.eventType}
                  onChange={(e) => handleChange('eventType', e.target.value)}
                  label="Tipo de Evento *"
                >
                  {Object.values(ESocialEventType).map((type) => (
                    <MenuItem key={type} value={type}>
                      {ESOCIAL_EVENT_TYPE_LABELS[type]}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.eventType && (
                  <FormHelperText>{validationErrors.eventType}</FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Employee (conditional) */}
            <Grid size={12}>
              <FormControl
                fullWidth
                error={!!validationErrors.employeeId}
                disabled={!formData.eventType}
              >
                <InputLabel>
                  Funcionário {requiresEmployee ? '*' : ''}
                </InputLabel>
                <Select
                  value={formData.employeeId}
                  onChange={(e) => handleChange('employeeId', e.target.value)}
                  label={`Funcionário ${requiresEmployee ? '*' : ''}`}
                >
                  <MenuItem value="">
                    {requiresEmployee ? 'Selecione...' : 'Nenhum'}
                  </MenuItem>
                  {employeeOptions.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id.toString()}>
                      {emp.fullName}
                    </MenuItem>
                  ))}
                </Select>
                {validationErrors.employeeId && (
                  <FormHelperText>{validationErrors.employeeId}</FormHelperText>
                )}
                {requiresEmployee && employeeOptions.length === 0 && (
                  <FormHelperText>
                    Nenhum funcionário disponível
                  </FormHelperText>
                )}
              </FormControl>
            </Grid>

            {/* Event Date */}
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                type="date"
                label="Data do Evento *"
                value={formData.eventDate}
                onChange={(e) => handleChange('eventDate', e.target.value)}
                error={!!validationErrors.eventDate}
                helperText={validationErrors.eventDate}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Due Date */}
            <Grid
              size={{
                xs: 12,
                sm: 6
              }}>
              <TextField
                fullWidth
                type="date"
                label="Data Limite"
                value={formData.dueDate}
                onChange={(e) => handleChange('dueDate', e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            {/* Notes */}
            <Grid size={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Observacoes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                inputProps={{ maxLength: 5000 }}
                helperText={`${formData.notes.length}/5000 caracteres`}
              />
            </Grid>
            {/* Event-specific metadata form */}
            {formData.eventType && ['S_2210', 'S_2220', 'S_2240'].includes(formData.eventType) && (
              <Grid size={12}>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                  Dados Especificos do Evento
                </Typography>
                {formData.eventType === 'S_2210' && (
                  <S2210Form metadata={metadata as Partial<S2210Metadata> | null} onChange={(m) => setMetadata(m as Record<string, unknown>)} />
                )}
                {formData.eventType === 'S_2220' && (
                  <S2220Form metadata={metadata as Partial<S2220Metadata> | null} onChange={(m) => setMetadata(m as Record<string, unknown>)} />
                )}
                {formData.eventType === 'S_2240' && (
                  <S2240Form metadata={metadata as Partial<S2240Metadata> | null} onChange={(m) => setMetadata(m as Record<string, unknown>)} />
                )}
              </Grid>
            )}
          </Grid>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Salvando...' : isEditing ? 'Salvar' : 'Criar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ESocialEventFormModal;
